import type {
  Block,
  HorizontalAlignment,
  Layer,
  LineType,
  Point2D,
  Point3D,
  Unit,
  VerticalAlignment,
} from 'dxf-writer'
import BlockRecord from 'dxf-writer/src/BlockRecord.js'
import DatabaseObject from 'dxf-writer/src/DatabaseObject.js'
import type Dictionary from 'dxf-writer/src/Dictionary.js'
import DxfDrawing from 'dxf-writer/src/Drawing.js'
import Handle from 'dxf-writer/src/Handle.js'
import Table from 'dxf-writer/src/Table.js'
import TagsManager from 'dxf-writer/src/TagsManager.js'
import TextStyle from 'dxf-writer/src/TextStyle.js'
import { DXFLineTypes } from './constant'

const MLEADER_ARROWHEAD_SIZE = 0

type ModelSpaceEntity = {
  layer?: Layer
  ownerObjectHandle?: string
  tags: (manager: TagsManager) => void
}

/**
 * DXF CLASS 段落记录定义，用于声明自定义实体类。
 */
export interface DxfClassDefinition {
  /** 应用程序层面的类名称，组码 1 */
  appClassName: string
  /** C++ 类名称，组码 2 */
  cppClassName: string
  /** 所属应用名称，组码 3 */
  appName: string
  /** 代理能力位标志，组码 90 */
  proxyCapabilities: number
  /** 实例计数，组码 91 */
  instanceCount: number
  /** 是否为代理对象，0/1，组码 280 */
  wasAProxy: number
  /** 是否为图形实体，0/1，组码 281 */
  isAnEntity: number
}

class DxfClassRecord {
  constructor(private readonly definition: DxfClassDefinition) {}

  tags(manager: TagsManager) {
    manager.push(0, 'CLASS')
    manager.push(1, this.definition.appClassName)
    manager.push(2, this.definition.cppClassName)
    manager.push(3, this.definition.appName)
    manager.push(90, this.definition.proxyCapabilities)
    manager.push(91, this.definition.instanceCount)
    manager.push(280, this.definition.wasAProxy)
    manager.push(281, this.definition.isAnEntity)
  }
}

type DxfObjectLike = DatabaseObject & {
  tags: (manager: TagsManager) => void
}

class DxfObjectDictionary extends DatabaseObject {
  declare handle: string
  private readonly entries = new Map<string, DxfObjectLike>()

  constructor() {
    super('AcDbDictionary')
  }

  addEntry(name: string, object: DxfObjectLike) {
    object.ownerObjectHandle = this.handle
    this.entries.set(name, object)
  }

  tags(manager: TagsManager) {
    manager.push(0, 'DICTIONARY')
    super.tags(manager)
    manager.push(281, 1)

    for (const [name, object] of this.entries) {
      manager.push(3, name)
      manager.push(350, object.handle)
    }

    for (const object of this.entries.values()) {
      object.tags(manager)
    }
  }
}

class DxfMLineStyle extends DatabaseObject {
  constructor() {
    super('AcDbMlineStyle')
  }

  tags(manager: TagsManager) {
    manager.push(0, 'MLINESTYLE')
    super.tags(manager)
    manager.push(2, 'Standard')
    manager.push(70, 0)
    manager.push(62, 256)
    manager.push(51, 90.0)
    manager.push(52, 90.0)
    manager.push(71, 2)
    manager.push(49, 0.5)
    manager.push(62, 256)
    manager.push(6, 'BYLAYER')
    manager.push(49, -0.5)
    manager.push(62, 256)
    manager.push(6, 'BYLAYER')
  }
}

class DxfMLeaderStyle extends DatabaseObject {
  declare handle: string

  constructor(
    private readonly textStyleHandle: string,
    private readonly lineTypeHandle: string
  ) {
    super('AcDbMLeaderStyle')
  }

  tags(manager: TagsManager) {
    manager.push(0, 'MLEADERSTYLE')
    super.tags(manager)
    manager.push(179, 2)
    manager.push(170, 2)
    manager.push(171, 1)
    manager.push(172, 0)
    manager.push(90, 2)
    manager.push(40, 0.0)
    manager.push(41, 0.0)
    manager.push(173, 1)
    manager.push(91, -1056964608)
    manager.push(340, this.lineTypeHandle)
    manager.push(92, -2)
    manager.push(290, 1)
    manager.push(42, 2.0)
    manager.push(291, 1)
    manager.push(43, 8.0)
    manager.push(3, 'Standard')
    manager.push(44, MLEADER_ARROWHEAD_SIZE)
    manager.push(300, '')
    manager.push(342, this.textStyleHandle)
    manager.push(174, 1)
    manager.push(178, 1)
    manager.push(175, 1)
    manager.push(176, 0)
    manager.push(93, -1056964608)
    manager.push(45, 4.0)
    manager.push(292, 0)
    manager.push(297, 0)
    manager.push(46, 4.0)
    manager.push(94, -1056964608)
    manager.push(47, 1.0)
    manager.push(49, 1.0)
    manager.push(140, 1.0)
    manager.push(293, 1)
    manager.push(141, 0.0)
    manager.push(294, 1)
    manager.push(177, 0)
    manager.push(142, 1.0)
    manager.push(295, 0)
    manager.push(296, 1)
    manager.push(143, 3.75)
    manager.push(271, 0)
    manager.push(272, 9)
    manager.push(273, 9)
  }
}

class DxfAppId extends DatabaseObject {
  constructor(private readonly name: string) {
    super(['AcDbSymbolTableRecord', 'AcDbRegAppTableRecord'])
  }

  tags(manager: TagsManager) {
    manager.push(0, 'APPID')
    super.tags(manager)
    manager.push(2, this.name)
    manager.push(70, 0)
  }
}

interface DxfShapeLike {
  dxfEntityName?: string
  constructor?: { name?: string }
}

// 自定义线型注册
DxfDrawing.LINE_TYPES = DXFLineTypes

/** 自定义 Drawing，扩展 dxf-writer 以支持 CLASS、MODEL SPACE 实体注册等 */
export default class Drawing extends DxfDrawing {
  /** 全局可用线型定义表，继承自 dxf-writer 并在模块加载时覆写 */
  static LINE_TYPES: { name: string; description: string; elements: number[] }[]
  /** AutoCAD ACI 颜色索引表 */
  static ACI: Record<string, number>
  private mlineStyleDictionary?: DxfObjectDictionary
  private mlineStyleObject?: DxfMLineStyle
  private mLeaderStyleDictionary?: DxfObjectDictionary
  private mLeaderStyleObject?: DxfMLeaderStyle
  private registeredMLeaderEntityCount: number = 0
  private readonly classDefinitions = new Map<string, DxfClassDefinition>()
  private readonly modelSpaceEntities: ModelSpaceEntity[] = []
  /** 名称到图层的索引 */
  declare layers: Record<string, Layer>
  /** 当前激活图层，未激活时为 null */
  declare activeLayer: Layer | null
  /** 名称到线型的索引 */
  declare lineTypes: Record<string, LineType>
  /** DXF 头段变量表 */
  declare headers: Record<string, [number, number][]>
  /** 设置头段变量 */
  declare header: (variable: string, values: [number, string | number][]) => this
  /** 名称到符号表的索引 */
  declare tables: Record<string, Table>
  /** 名称到块的索引 */
  declare blocks: Record<string, Block>
  /** 根字典对象 */
  declare dictionary: Dictionary
  /** 默认 MODEL_SPACE 块，含句柄 */
  declare modelSpace: Block & { handle: string }

  /** 匿名标注块序列号 */
  private anonDimBlockSeq: number = 0

  /** 申请下一个匿名标注块名（DXF 规范：以 `*D` 前缀） */
  getAnonDimBlockName(): string {
    return `*D${++this.anonDimBlockSeq}`
  }

  /** 添加自定义线型 */
  declare addLineType: (name: string, description: string, elements: number[]) => this
  declare _ltypeTable: () => Table
  declare _layerTable: (manager?: TagsManager) => Table
  /** 设置图纸单位 */
  declare setUnits: (unit: Unit) => this
  /** 生成 AutoCAD 兼容的额外段 */
  declare generateAutocadExtras: () => void
  /** 输出完整 DXF 字符串 */
  declare toDxfString: () => string
  /** 新增图层 */
  declare addLayer: (name: string, colorNumber: number, lineTypeName: string) => this
  /** 切换当前激活图层 */
  declare setActiveLayer: (name: string) => this
  /** 在当前图层绘制直线 */
  declare drawLine: (x1: number, y1: number, x2: number, y2: number) => this
  /** 绘制三维直线 */
  declare drawLine3d: (
    x1: number,
    y1: number,
    z1: number,
    x2: number,
    y2: number,
    z2: number
  ) => this
  /** 绘制点 */
  declare drawPoint: (x: number, y: number) => this
  /** 绘制矩形，支持倒角 */
  declare drawRect: (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    cornerLength?: number,
    cornerBulge?: number
  ) => this
  /** 绘制正多边形 */
  declare drawPolygon: (
    x: number,
    y: number,
    numberOfSides: number,
    radius: number,
    rotation?: number,
    circumscribed?: boolean
  ) => this
  /** 绘制圆弧 */
  declare drawArc: (x1: number, y1: number, r: number, startAngle: number, endAngle: number) => this
  /** 绘制圆 */
  declare drawCircle: (x1: number, y1: number, r: number) => this
  /** 绘制圆柱体 */
  declare drawCylinder: (
    x1: number,
    y1: number,
    z1: number,
    r: number,
    thickness: number,
    extrusionDirectionX: number,
    extrusionDirectionY: number,
    extrusionDirectionZ: number
  ) => this
  /** 绘制单行文本 */
  declare drawText: (
    x1: number,
    y1: number,
    height: number,
    rotation: number,
    value: string,
    horizontalAlignment?: HorizontalAlignment,
    verticalAlignment?: VerticalAlignment
  ) => this
  /** 绘制二维多段线 */
  declare drawPolyline: (
    points: Point2D[],
    closed?: boolean,
    startWidth?: number,
    endWidth?: number
  ) => this
  /** 绘制三维多段线 */
  declare drawPolyline3d: (points: Point3D[]) => this
  /** 设置真彩色 */
  declare setTrueColor: (trueColor: number) => this
  /** 绘制样条曲线 */
  declare drawSpline: (
    controlPoints: number[][],
    degree?: number,
    knots?: number[] | null,
    weights?: number[] | null,
    fitPoints?: number[][]
  ) => this
  /** 绘制椭圆或椭圆弧 */
  declare drawEllipse: (
    x1: number,
    y1: number,
    majorAxisX: number,
    majorAxisY: number,
    axisRatio: number,
    startAngle?: number,
    endAngle?: number
  ) => this
  /** 绘制四点面 */
  declare drawFace: (
    x1: number,
    y1: number,
    z1: number,
    x2: number,
    y2: number,
    z2: number,
    x3: number,
    y3: number,
    z3: number,
    x4: number,
    y4: number,
    z4: number
  ) => this
  /** 向当前图层追加任意形状 */
  declare addShape: (shape: any) => void
  /** 注册新的符号表 */
  declare addTable: (name: string) => Table
  /** 注册新的块 */
  declare addBlock: (name: string) => Block

  constructor() {
    super()
    this.init()
  }

  /** 修复样式表默认样式：重命名为 Standard 并切换字体，避免中文乱码 */
  private init() {
    let styleTable = this.tables['STYLE']
    if (!styleTable) {
      styleTable = this.addTable('STYLE')
    }
    const defaultStyle = (styleTable.elements[0] || new TextStyle('Standard')) as TextStyle
    defaultStyle.name = 'Standard'
    defaultStyle.fontFileName = 'arial.ttf'
  }

  /**
   * 注册 DXF CLASS 定义。
   *
   * @param definition - 自定义类定义
   * @returns 当前 Drawing 实例，便于链式调用
   */
  addDxfClass(definition: DxfClassDefinition): this {
    this.classDefinitions.set(definition.appClassName, definition)
    return this
  }

  addAppId(name: string): this {
    const appIdTable = this.tables['APPID'] ?? this.addTable('APPID')
    const elements = appIdTable.elements as Array<{ name?: string }>
    if (!elements.some((element) => element.name === name)) {
      appIdTable.add(new DxfAppId(name))
    }
    return this
  }

  /** 记录待导出的 MULTILEADER 实例，用于在 CLASSES 段注册 AcDbMLeader。 */
  registerMLeaderEntity(): this {
    this.registeredMLeaderEntityCount += 1
    this.ensureMLeaderEntityClass()
    return this
  }

  /**
   * 向 MODEL SPACE 添加实体。
   *
   * @param shape - 待插入的 model space 实体
   * @param prepend - 为 true 时插入到队首，默认追加到队尾
   * @returns 当前 Drawing 实例，便于链式调用
   */
  addModelSpaceEntity(shape: ModelSpaceEntity, prepend = false): this {
    if (prepend) {
      this.modelSpaceEntities.unshift(shape)
    } else {
      this.modelSpaceEntities.push(shape)
    }
    return this
  }

  private ensureMLineStyleObjects() {
    if (this.mlineStyleDictionary) {
      return
    }

    const existingDictionary = this.dictionary.children?.ACAD_MLINESTYLE
    if (existingDictionary instanceof DxfObjectDictionary) {
      this.mlineStyleDictionary = existingDictionary
      return
    }

    const mlineStyleDictionary = new DxfObjectDictionary()
    const mlineStyleObject = new DxfMLineStyle()
    mlineStyleDictionary.addEntry('Standard', mlineStyleObject)
    this.dictionary.addChildDictionary('ACAD_MLINESTYLE', mlineStyleDictionary)
    this.mlineStyleDictionary = mlineStyleDictionary
    this.mlineStyleObject = mlineStyleObject
  }

  private ensureMLeaderStyleObjects() {
    if (this.mLeaderStyleDictionary) {
      return
    }

    this.classDefinitions.set('MLEADERSTYLE', {
      appClassName: 'MLEADERSTYLE',
      cppClassName: 'AcDbMLeaderStyle',
      appName: 'ACDB_MLEADERSTYLE_CLASS',
      proxyCapabilities: 4095,
      instanceCount: 1,
      wasAProxy: 0,
      isAnEntity: 0,
    })

    const existingDictionary = this.dictionary.children?.ACAD_MLEADERSTYLE
    if (existingDictionary instanceof DxfObjectDictionary) {
      this.mLeaderStyleDictionary = existingDictionary
      return
    }

    const textStyle = (this.tables['STYLE']?.elements?.[0] ??
      new TextStyle('Standard')) as TextStyle
    const byBlockLineTypeHandle =
      (this.lineTypes?.ByBlock as unknown as { handle?: string })?.handle ?? '0'
    const mLeaderStyleDictionary = new DxfObjectDictionary()
    const mLeaderStyleObject = new DxfMLeaderStyle(
      (textStyle as unknown as { handle: string }).handle,
      byBlockLineTypeHandle
    )
    mLeaderStyleDictionary.addEntry('Standard', mLeaderStyleObject)
    this.dictionary.addChildDictionary('ACAD_MLEADERSTYLE', mLeaderStyleDictionary)
    this.mLeaderStyleDictionary = mLeaderStyleDictionary
    this.mLeaderStyleObject = mLeaderStyleObject
  }

  private ensureMLeaderEntityClass() {
    const instanceCount = Math.max(this.registeredMLeaderEntityCount, this.countMLeaderShapes())
    if (instanceCount <= 0) {
      return
    }

    this.classDefinitions.set('MULTILEADER', {
      appClassName: 'MULTILEADER',
      cppClassName: 'AcDbMLeader',
      appName: 'ACDB_MLEADER_CLASS',
      proxyCapabilities: 1025,
      instanceCount,
      wasAProxy: 0,
      isAnEntity: 1,
    })
  }

  private countMLeaderShapes() {
    const layerCount = Object.values(this.layers ?? {}).reduce((count, layer) => {
      const shapes = (layer as unknown as { shapes?: unknown[] }).shapes ?? []
      return count + shapes.filter((shape) => this.isMLeaderShape(shape)).length
    }, 0)
    const modelSpaceCount = this.modelSpaceEntities.filter((shape) =>
      this.isMLeaderShape(shape)
    ).length
    return layerCount + modelSpaceCount
  }

  private isMLeaderShape(shape: unknown) {
    const candidate = shape as DxfShapeLike | null
    return candidate?.dxfEntityName === 'MULTILEADER' || candidate?.constructor?.name === 'MLeader'
  }

  getMLeaderStyleHandle(): string {
    this.ensureMLeaderStyleObjects()
    return this.mLeaderStyleObject?.handle ?? '0'
  }

  /**
   * 构建并填充 TagsManager，生成包含 HEADER / CLASSES / TABLES / BLOCKS / ENTITIES / OBJECTS 的完整段序列。
   *
   * @returns 已按 DXF 段顺序写入所有内容的 TagsManager
   * @internal
   */
  _tagsManager(): TagsManager {
    this.ensureMLineStyleObjects()
    this.ensureMLeaderStyleObjects()
    this.ensureMLeaderEntityClass()
    const manager = new TagsManager()

    const blockRecordTable = new Table('BLOCK_RECORD')
    const blocks = Object.values(this.blocks ?? {})
    for (const block of blocks) {
      const record = new BlockRecord((block as unknown as { name: string }).name)
      blockRecordTable.add(record)
      ;(block as any).ownerObjectHandle = record.handle
      if ((block as any).end) {
        ;(block as any).end.ownerObjectHandle = record.handle
      }
      if ((block as any).recordHandle !== undefined) {
        ;(block as any).recordHandle = record.handle
      }
    }
    const ltypeTable = this._ltypeTable()
    const layerTable = this._layerTable(manager)

    // Header section
    manager.start('HEADER')
    manager.addHeaderVariable('HANDSEED', [[5, Handle.peek()]])
    const variables = Object.entries(this.headers ?? {})
    for (const [name, values] of variables) {
      manager.addHeaderVariable(name, values as [number, number][])
    }
    manager.end()

    // Classes section
    manager.start('CLASSES')
    for (const classDefinition of this.classDefinitions.values()) {
      new DxfClassRecord(classDefinition).tags(manager)
    }
    manager.end()

    // Tables section
    manager.start('TABLES')
    ltypeTable.tags(manager)
    layerTable.tags(manager)
    const tables = Object.values(this.tables ?? {})
    for (const table of tables) {
      table.tags(manager)
    }
    blockRecordTable.tags(manager)
    manager.end()

    // Blocks section
    manager.start('BLOCKS')
    for (const block of blocks) {
      block.tags(manager)
    }
    manager.end()

    // Entities section
    manager.start('ENTITIES')
    for (const entity of this.modelSpaceEntities) {
      entity.ownerObjectHandle = this.modelSpace.handle
      entity.tags(manager)
    }
    const layers = Object.values(this.layers ?? {})
    for (const layer of layers) {
      layer.shapesTags(this.modelSpace, manager)
    }
    manager.end()

    // Objects section
    manager.start('OBJECTS')
    this.dictionary.tags(manager)
    manager.end()

    // EOF
    manager.push(0, 'EOF')
    return manager
  }
}
